import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  makeStyles,
  Grid,
  Button,
} from '@material-ui/core';

import { Controller, useForm } from 'react-hook-form';
import { FileInput } from '@ofqual/portal-components/components/FileInput';
import { MarkdownEditor } from '@ofqual/portal-components/components/MarkdownEditor';
import { Error } from '../../../common/Error';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addCaseNote } from '../../../../redux/actions';
import { SocItemModel } from '../../../../domain/SocItem';
import { segmentList } from '@ofqual/portal-core/core/utils/listHelpers';
import { formatAsTodayYesterdayOrDate } from '@ofqual/portal-core/core/utils/dateUtils';
import {
  Activity,
  ActivityFeedItem,
  ActivityFeedItemDefinition,
  ActivitySegmentHeader,
} from '@ofqual/portal-activity-feed';
import { buildDefinitions } from '../buildDefinitions';
import { feedSelector } from '../../../../redux/selectors';
import { ActivityType } from '../../../../domain/ActivityType';
import { Select } from '@ofqual/portal-components/components/Select';
import { useLookup } from '@ofqual/portal-core/core/hooks';
import { noteTypesService } from '../../../../services';
import { MAX_TOTAL_SIZE_MB, validateFiles } from "../../../utils/fileSizeLimit"

const useStyles = makeStyles((_) => ({
  actions: {
    padding: 15,
  },
}));

export const CaseNoteDialog: React.FC<{
  onClose: () => void;
  resource: SocItemModel;
}> = ({ onClose, resource }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const { handleSubmit, control, errors } = useForm({
    defaultValues: {
      message: null,
      files: [],
      note: null,
      noteType: null,
    },
  });

  const [noteTypesLookups] = useLookup(noteTypesService);

  const handleAddNote = (data: {
    files: File[];
    note: string;
    noteType: number;
  }) => {
    dispatch(addCaseNote(resource.id, data.files, data.note, data.noteType));
    onClose();
  };

  const definitions: ActivityFeedItemDefinition<any>[] = buildDefinitions(
    () => { },
    resource,
    noteTypesLookups,
  );

  const Values = (): Activity[] => {
    const activity = useSelector(feedSelector);
    const value = activity[resource.id];
    const items = value.response.data
      .filter((item) => item.activityType === ActivityType.SocCaseNote)
      .sort((a, b) => {
        let dateA = new Date(a.createdDate);
        let dateB = new Date(b.createdDate);
        return dateB.getTime() - dateA.getTime();
      });

    return items;
  };

  const segmentedActivities = segmentList(
    Values(),
    (item) => formatAsTodayYesterdayOrDate(item.createdDate) || '',
  );

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth={segmentedActivities.length > 0 ? 'lg' : 'sm'}
      fullWidth
    >
      <DialogTitle data-test="activity-title">Add a note</DialogTitle>
      <Divider />
      <DialogContent style={{ display: 'flex', minHeight: '350px' }}>
        <Grid container spacing={2} style={{ height: '100%' }}>
          {/* Left side: Form */}
          <Grid
            item
            xs={12}
            md={segmentedActivities.length > 0 ? 7 : 12}
            style={{ paddingRight: '10px' }}
          >
            <form>
              <Grid container spacing={3} style={{ marginTop: '10px' }}>
                <Grid item xs={12}>
                  <Controller
                    control={control}
                    name="noteType"
                    rules={{ required: true }}
                    as={
                      <Select
                        label="Note Type"
                        data-test="notes-case-type-field"
                      >
                        {noteTypesLookups}
                      </Select>
                    }
                  />
                  {errors.noteType && (
                    <Error data-test="note-type-field-error">
                      This field is required
                    </Error>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    control={control}
                    name="note"
                    rules={{ required: true }}
                    render={({ onChange, value }) => (
                      <MarkdownEditor
                        onChange={onChange}
                        value={value}
                        dataTest="note-text-field"
                      />
                    )}
                  />
                  {errors.note && (
                    <Error data-test="note-text-field-error">
                      This field is required
                    </Error>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    control={control}
                    name="files"
                    as={
                      <FileInput
                        multiple
                        dataTest="note-add-attachment-button"
                      />
                    }
                    onChange={([event]: any) => {
                      const target = event.target as HTMLInputElement;
                      return target.files ? Array.from(target.files) : [];
                    }}
                    rules={{ validate: validateFiles }}
                  />
                  {errors.files && (
                    <div style={{ marginTop: '15px' }}>
                      <Error data-test="case-note-file-validation">
                        The total size of the files exceeds the limit of {MAX_TOTAL_SIZE_MB}MB. Please upload smaller files.
                      </Error>
                    </div>
                  )}
                </Grid>
              </Grid>
            </form>
          </Grid>
          {/* Right side: Activities */}
          {segmentedActivities.length > 0 ? (
            <Grid
              item
              xs={12}
              md={5}
              style={{ minHeight: '350px', position: 'relative' }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: 0,
                  right: 0,
                  overflowY: 'auto',
                  paddingRight: '10px',
                }}
              >
                {segmentedActivities.map((segment) => (
                  <div key={segment.label} style={{ marginTop: '10px' }}>
                    <ActivitySegmentHeader label={segment.label} />
                    {segment.data.map((item) => (
                      <ActivityFeedItem
                        key={item.id}
                        activity={item}
                        definition={definitions.find(
                          (def) => def.activityType === item.activityType,
                        )}
                      />
                    ))}
                  </div>
                ))}
              </div>
            </Grid>
          ) : null}
        </Grid>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button
          onClick={onClose}
          color="primary"
          data-test="close-add-note-dialog"
        >
          Close
        </Button>
        <Button
          onClick={handleSubmit(handleAddNote)}
          color="primary"
          data-test="add-note-button"
        >
          Add
        </Button>
      </DialogActions>
      <Divider />
    </Dialog>
  );
};
